import React, { useCallback } from "react";

import { Button } from "../../../../Shared/Buttons/Button";
import { dmnIdentity } from "../../../../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../../../../utils/environment";
import dayjs from "dayjs";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { document } from "@workspace/mobile";
import { Switch } from "../../../../ui/switch";
import {
  errorHandler,
  apiFetchNotOkHandler
} from "../../../../../APIs/errorHandler";
/**
 * DownloadKitchenSheetContent Props
 */
type DownloadKitchenSheetContentProps = {
  onClose?: () => void;
  onBack?: () => void;
};

/**
 * DownloadKitchenSheetContent Modal
 *
 * @param param0
 * @returns
 */
export const DownloadKitchenSheetContent = ({
  onBack,
  onClose
}: DownloadKitchenSheetContentProps) => {
  const { venueConfig, date } = useDiaryContext();
  const [includeOpenEnquiries, setIncludeOpenEnquiries] =
    React.useState<boolean>(false);

  const handleBack = () => {
    onBack?.();
  };

  const handleDownload = useCallback(async () => {
    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);

    const url = new URL(
      `${environmentVars?.CORE_API_BASE_URL}/booking-reports/kitchen-sheet/`
    );
    url.searchParams.set("date", dayjs(date).format("YYYY-MM-DD"));
    url.searchParams.set("output", "csv");
    url.searchParams.set(
      "status",
      includeOpenEnquiries ? "in_progress,complete" : "complete"
    );
    url.searchParams.set("source", "designmynight-mobile");
    url.searchParams.set("debug", "dmn");
    url.searchParams.set("venue_id", venueConfig?._id ?? "");

    try {
      const request = await fetch(url, {
        headers: {
          authorization: `Bearer ${token}`,
          accept: "*/*"
        },
        method: "GET"
      });
      if (!request.ok) {
        apiFetchNotOkHandler(request, "Error downloading kitchen sheet");
        return;
      }
      // Further processing...

      const arrBuffer = await request.arrayBuffer();
      const file = new Blob([arrBuffer], { type: "text/csv" });
      const fileReader = new FileReader();

      fileReader.onloadend = async () => {
        // Parse out the result to get the document data.
        const result = fileReader.result as string;
        const [, mimeType, , data] = result.split(/[:;,]+/);
        const fileName = `kitchen-sheet.csv`;
        document.open({ mimeType, data, fileName });
      };

      fileReader.readAsDataURL(file);
    } catch (error) {
      errorHandler(error, "Error downloading kitchen sheet");

      return;
    }

    onClose?.();
  }, [includeOpenEnquiries, date, venueConfig]);

  return (
    <div className="DownloadFileCotainer flex flex-col flex-1">
      <div className="DownloadFileContent flex-1 flex flex-col">
        <p>Shows information designed for the kitchen.</p>
        <p>
          <strong>Include open enquiries?</strong>
        </p>
        <div>
          <Switch
            name={"unconformed"}
            checked={includeOpenEnquiries}
            onCheckedChange={(checked) => {
              setIncludeOpenEnquiries(checked);
            }}
          />
        </div>
      </div>
      <div className="DownloadFileFooter flex-none flex flex-row gap-2.5">
        <Button className="DownloadFileFooterButton" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="DownloadFileFooterButton"
          onClick={handleDownload}
          type="primary"
        >
          Download Kitchen Sheet
        </Button>
      </div>
    </div>
  );
};
